import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Typography, Grid, withStyles } from '@material-ui/core';
import { TextButton, FilledButton, Alert } from '@ds/react-components';
import PageWrapper from 'components/ui/PageWrapper';
import Table from 'components/ui/Table';
import { deleteSsoGroup } from 'services/ssoGroups';
import tableMetadata from './tableMetaData';
import SsoGroupListFilter from './SsoGroupListFilter';
import SsoGroupDeleteConfirmDialog from '../Delete/SsoGroupDeleteConfirmDialog';

const styles = (theme) => ({
  row: {
    cursor: 'pointer',
  },
  pageHeader: {
    marginBottom: '20px',
  },
  errorContainer: {
    marginTop: '16px',
    marginBottom: '16px',
    maxWidth: '910px',
  },
  deleteButton: {
    color: theme.palette.xpl.text.warning,
  },
});

const SsoGroupList = (props) => {
  const { getSsoGroups, appLoading, classes, groups } = props;
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formState, setFormState] = useState({
    currentPage: 0,
    filteredGroups: null,
  });

  const history = useHistory();

  const processGroupData = (groups) => {
    return groups.map((r) => {
      const group = { ...r };
      group.id = group.groupId;
      group.groupName = group.name;
      group.groupDescription = group.description;
      group.action = (
        <Grid container spacing={1} justify="flex-end">
          <Grid>
            <TextButton
              onClick={(event) => handleEditGroupClick(event, group.id)}>
              Edit
            </TextButton>
            <TextButton
              className={classes.deleteButton}
              style={{ marginLeft: '1em' }}
              onClick={(event) => handleDeleteGroupClick(event, group.id)}>
              Delete
            </TextButton>
          </Grid>
        </Grid>
      );
      return group;
    });
  };

  useEffect(() => {
    const getGroups = async () => {
      await getSsoGroups();
    };

    getGroups();
  }, [getSsoGroups]);

  const groupsData =
    formState.filteredGroups === null ? groups : formState.filteredGroups;

  const ssoGroups = useMemo(() => processGroupData(groupsData), [groupsData]);

  const showNoResults =
    !appLoading &&
    formState.filteredGroups !== null &&
    formState.filteredGroups.length === 0;

  const formatText = (text) => {
    // Replace hyphen symbols and double spaces if any
    return (text || '').replace(/-/g, ' ').replace(/  +/g, ' ');
  };

  const handleFilter = (data) => {
    const { search } = data;
    let searchResult = groups;

    if (search) {
      searchResult = groups.filter((item) => {
        const groupName = formatText(item.name).toLowerCase();
        const formattedSearch = formatText(search).toLowerCase();
        return groupName.includes(formattedSearch);
      });
    }

    setFormState((prevState) => ({
      ...prevState,
      currentPage: 0,
      filteredGroups: searchResult,
    }));
  };

  const getGroupName = (id) => {
    const group = ssoGroups.find((x) => x.id === id);
    return group?.name;
  };

  const handleAddGroupClick = (event) => {
    event.preventDefault();
    history.push(`/SsoGroupManagement/Add`);
  };

  const handleEditGroupClick = (event, id) => {
    event.preventDefault();
    history.push(`/SsoGroupManagement/${id}/Edit`);
  };

  const handlePageClick = (data) => {
    setFormState((prevState) => ({
      ...prevState,
      currentPage: data.selected,
    }));
  };

  const handleDeleteGroupClick = (event, id) => {
    event.preventDefault();
    setDeletingGroupId(id);
    setDeleteDialogOpen(true);
  };

  const onDelete = async (groupId) => {
    try {
      await deleteSsoGroup(groupId);
      setDeleteDialogOpen(false);
      setErrorMessage('');
      await getSsoGroups();
    } catch (error) {
      setErrorMessage('Error occurred when deleting group. Please try again.');
      console.error(error);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <PageWrapper>
      {errorMessage && (
        <>
          <Alert
            className={classes.errorContainer}
            variant={'error'}
            role={'banner'}
            dismissible={false}>
            <Alert.AlertMessage>{errorMessage}</Alert.AlertMessage>
          </Alert>
        </>
      )}

      <Grid container spacing={2}>
        <Grid item>
          <Typography
            variant="h2"
            color="inherit"
            className={classes.pageHeader}
            noWrap>
            Group Management
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: 'auto' }}>
          <FilledButton onClick={handleAddGroupClick}>Add Group</FilledButton>
        </Grid>
      </Grid>
      {!appLoading && (
        <>
          <SsoGroupDeleteConfirmDialog
            groupName={getGroupName(deletingGroupId)}
            onDelete={() => onDelete(deletingGroupId)}
            isOpen={isDeleteDialogOpen}
            onClose={closeDeleteDialog}></SsoGroupDeleteConfirmDialog>
          <SsoGroupListFilter onSubmit={handleFilter}></SsoGroupListFilter>
          <Grid container spacing={1} justify="flex-start">
            <Grid item xs={12}>
              <Table
                data={ssoGroups}
                metadata={tableMetadata}
                orderBy={'groupName'}
                classes={{ row: classes.row }}
                showNoResults={showNoResults}
                enableSorting={true}
                showFooter={true}
                noBackground={false}
                showEmptyRows={true}
                currentPage={formState.currentPage}
                rowsPerPage={10}
                onPageChange={handlePageClick}
                footerText={
                  <div>
                    <b>{ssoGroups.length}</b> Results Found
                  </div>
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </PageWrapper>
  );
};

SsoGroupList.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
  getSsoGroups: PropTypes.func.isRequired,
  appLoading: PropTypes.bool.isRequired,
};

SsoGroupList.defaultProps = {
  groups: [],
};

export default withStyles(styles)(SsoGroupList);
